import { Injectable, inject } from '@angular/core';
import { ConfigResource } from '@df/catalog/config/config.resource';
import { App } from '@df/core/app';
import { WINDOW } from '@df/dom/globals';
import { Nexus } from '@tomandco/nexus-api-types';
import { NexusApiService } from '../nexus/nexus-api.service';
import { RequestService } from '../request/request.service';
import { getInitVersion } from '../util/get-init-version';

declare module '@df/core/app' {
    interface App {
        apiConfigService: DfApiConfigService;
    }
}

@Injectable({
    providedIn: 'root'
})
export class DfApiConfigService {
    _countries: Nexus.Api.General.Countries.Response | undefined;
    private _apiConfig: Nexus.Lib.General.AppData | undefined;

    private window = inject(WINDOW);

    constructor(
        private app: App,
        private nexusApiService: NexusApiService,
        private requestService: RequestService,
        private configResource: ConfigResource
    ) {
        app.apiConfigService = this;
    }

    get countries(): Nexus.Lib.General.Country[] | undefined {
        return this._countries?.countries;
    }

    get apiConfig(): Nexus.Lib.General.AppData | undefined {
        return this._apiConfig;
    }

    async loadApiConfig(): Promise<Nexus.Lib.General.AppData> {
        // already loaded
        if (!this._apiConfig) {
            // nexus
            if (this.app.dfConfig.integration.backend === 'nexus') {
                this._apiConfig = await this.nexusApiService.general.appData();
            } else {
                // init.js case
                const autoInitData = this.window.autoInitData;
                if (autoInitData) {
                    this._apiConfig = autoInitData.website;
                }
                // mongo config case
                else {
                    const config = await this.configResource.loadModelByUrl('appData');
                    this._apiConfig = config!.data!.content;
                }
            }
        }
        return this._apiConfig!;
    }

    async loadCountries(): Promise<Nexus.Api.General.Countries.Response> {
        // already loaded
        if (!this._countries) {
            // nexus
            if (this.app.dfConfig.integration.backend === 'nexus') {
                this._countries = await this.nexusApiService.general.countries();
            } else {
                // init.js case
                if (this.window.autoInitData) {
                    this._countries = await this.requestService.sendXHR<Nexus.Api.General.Countries.Response>(
                        `static/js/${this.app.dfConfig.ms.siteId}/more.json${getInitVersion()}`,
                        undefined,
                        {
                            method: 'GET'
                        }
                    );
                }
                // mongo config case
                else {
                    const config = await this.configResource.loadCountries();
                    this._countries = { countries: config!.data!.content };
                }
            }
        }
        return this._countries;
    }
}
